import {Button} from "@mui/material";
import {useAuth} from "react-oidc-context";
import {useTranslation} from "react-i18next";
import {type CockpitUser} from "../../model/user/CockpitUser.ts";
import NewsSlider from "./NewsSlider";
import {store} from "../../store";
import authService from "../../service/authService.ts";
import {useEffect} from "react";
import BrandMarkSVG from "../../components/icons/BrandMarkSVG.tsx";
import {brandColors} from "../../theme/brandColors.ts";
import {devError} from "../../utils/dev.ts";

const LoginPage = () => {
    const auth = useAuth();
    const {t, i18n} = useTranslation();

    useEffect(() => {
        if (i18n.language !== window.navigator.language) {
            void i18n.changeLanguage(window.navigator.language);
        }
    }, [i18n]);

    const handleLogin = () => {
        void auth.signinPopup()
            .then(user => {
                const cockpitUser = user as CockpitUser;
                store.dispatch(authService.loginSuccess(
                    cockpitUser.profile,
                    cockpitUser.access_token,
                    cockpitUser.profile["user-scope"]
                ));
            })
            .catch(error => {
                devError(error);
            });
    };

    return (
        <div className="min-h-screen flex bg-an-bg font-lato">
            <div className="flex-[0_0_58%] flex flex-col justify-center items-center px-12 py-16 relative">
                <div className="absolute top-8 left-8">
                    <BrandMarkSVG className="block h-10 w-auto"/>
                </div>

                <div className="w-full max-w-[440px] flex flex-col gap-8">
                    <div>
                        <h1 className="an-h1 mb-3">
                            {t("login.appName")}
                        </h1>
                        <p className="an-body">
                            {t("login.subTitle")}
                        </p>
                    </div>

                    <Button
                        id="loginButton"
                        type="button"
                        onClick={handleLogin}
                        sx={{
                            "&.MuiButton-root": {width: "100%", minWidth: 0},
                            height: "56px",
                            background: brandColors.green200,
                            color: brandColors.ink,
                            borderRadius: "4px",
                            fontFamily: "'Lato', system-ui, sans-serif",
                            fontWeight: 700,
                            fontSize: "16px",
                            textTransform: "none",
                            boxShadow: "none",
                            "&:hover": {background: "#3CB28F", boxShadow: "none"},
                        }}
                    >
                        {t("login.registerButton")}
                    </Button>

                    <div className="text-xs text-an-muted-3 font-lato">
                        <span id="year">{new Date().getFullYear()}</span> {t("login.copyright")}
                    </div>
                </div>
            </div>
            <div
                className="flex-[0_0_42%] min-w-[320px] hidden md:flex flex-col text-white relative overflow-hidden"
                style={{background: "var(--an-gradient-hero)"}}
            >
                <div className="flex-1 flex flex-col justify-center px-12 py-16">
                    <NewsSlider/>
                </div>
                <div className="px-12 pb-12">
                    <div className="border-t border-white/30 mb-6"/>
                    <div className="text-sm text-white/90 leading-6">
                        {t("login.news.footerText")}{" "}
                        <a
                            href="https://zubehoer-navigator.de"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white underline hover:no-underline"
                        >
                            {t("login.news.footerLinkText")}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
